import React from 'react';
import './KPICard.css';
import KpiGapBadge from './dashboard/KpiGapBadge';
import { formatMetricValue, formatPercent } from '../utils/formatMetric';
import { getKpiStatusLabel, getKpiStatusTone } from '../utils/statusTone';

function KPICard({
  title,
  actualValue,
  goalValue,
  secondaryActualValue = null,
  secondaryGoalValue = null,
  value,
  target,
  unit = '',
  isHigherBetter = true,
  variance = null,
  percentToGoal = null,
  rollingProgress = null,
  elapsedPercent = null,
  status = 'no_data',
  actualLabel = 'Actual',
  goalLabel = 'Goal',
  secondaryActualLabel = '',
  secondaryGoalLabel = '',
  variant = 'standard',
  accentIndex = 0
}) {
  const resolvedActualValue = typeof actualValue === 'number' ? actualValue : value;
  const resolvedGoalValue = typeof goalValue === 'number' ? goalValue : target;
  const numValue = typeof resolvedActualValue === 'number' ? resolvedActualValue : null;
  const numTarget = typeof resolvedGoalValue === 'number' ? resolvedGoalValue : null;
  const normalizedPercent = Number.isFinite(percentToGoal) ? percentToGoal : 0;
  const hasGoal = Number.isFinite(numTarget);
  const hasSecondary = Number.isFinite(secondaryActualValue) || Number.isFinite(secondaryGoalValue);
  const normalizedProgress = Number.isFinite(rollingProgress) ? rollingProgress : normalizedPercent;
  const progressWidth = Math.max(0, Math.min(normalizedProgress, 140));
  const expectedPaceValue = hasGoal && Number.isFinite(elapsedPercent)
    ? numTarget * (elapsedPercent / 100)
    : null;

  const statusVariant = getKpiStatusTone(status);
  const statusLabel = getKpiStatusLabel(status);

  const formatValue = (amount) => {
    if (!Number.isFinite(amount)) return 'Pending';
    return formatMetricValue(amount, unit);
  };

  const formatGoalValue = (amount) => {
    if (!Number.isFinite(amount)) return 'Unassigned';
    return formatMetricValue(amount, unit);
  };

  const formatGoalPercent = (amount) => {
    if (!hasGoal) return 'Unassigned';
    if (!Number.isFinite(amount)) return 'Pending';
    return formatPercent(amount);
  };

  const getIcon = () => {
    if (statusVariant === 'good') return 'fa-check-circle';
    if (statusVariant === 'warning') return 'fa-triangle-exclamation';
    if (statusVariant === 'fair') return 'fa-chart-line';
    return 'fa-circle-info';
  };

  const paceRisk = !hasGoal || !Number.isFinite(rollingProgress)
    ? { tone: 'neutral', label: 'Pace N/A' }
    : rollingProgress >= 98
      ? { tone: 'good', label: 'On Pace' }
      : rollingProgress >= 85
        ? { tone: 'fair', label: 'Watch' }
        : { tone: 'warning', label: 'Off Pace' };

  if (variant === 'overview') {
    return (
      <div className={`kpi-card kpi-card-overview ${statusVariant} accent-${accentIndex % 4}`}>
        <div className="kpi-card-header">
          <div className="kpi-card-title-wrap">
            <div className="kpi-card-title">{title}</div>
          </div>
          <span className={`kpi-status-badge ${statusVariant}`}>{paceRisk.label}</span>
        </div>

        <div className="kpi-card-overview-value">
          <small>{actualLabel}</small>
          <strong className={!Number.isFinite(numValue) ? 'is-empty' : ''}>
            {Number.isFinite(numValue) ? formatValue(numValue) : 'Pending'}
          </strong>
        </div>

        <div className="kpi-card-overview-stat-row">
          <div className="kpi-card-overview-goal">
            <small>{goalLabel}</small>
            <strong className={!Number.isFinite(numTarget) ? 'is-empty' : ''}>{formatGoalValue(numTarget)}</strong>
          </div>
          <div className="kpi-card-overview-percent-block">
            <small>% to Goal</small>
            <strong className={`kpi-card-overview-percent ${!hasGoal ? 'is-empty' : ''}`}>{formatGoalPercent(percentToGoal)}</strong>
          </div>
        </div>

        <div className="kpi-card-progress">
          <div className="kpi-card-progress-track">
            <div
              className={`kpi-card-progress-fill ${statusVariant}`}
              style={{ width: `${hasGoal ? progressWidth : 0}%` }}
            />
          </div>
        </div>

        <div className="kpi-card-overview-footer">
          <div className="kpi-card-overview-meta-copy">
            <span className="kpi-card-overview-meta-label">Expected Pace</span>
            <span className="kpi-card-overview-pace-copy">{formatGoalValue(expectedPaceValue)}</span>
          </div>
          <div className="kpi-card-overview-meta-copy align-end">
            <span className="kpi-card-overview-meta-label">Status</span>
            <span className="kpi-card-overview-pace-copy">{statusLabel}</span>
          </div>
        </div>

        {hasSecondary ? (
          <div className="kpi-card-secondary-row kpi-card-overview-secondary">
            <div className="kpi-card-secondary-item">
              <small>{secondaryActualLabel}</small>
              <strong>{formatValue(secondaryActualValue)}</strong>
            </div>
            <div className="kpi-card-secondary-item">
              <small>{secondaryGoalLabel}</small>
              <strong>{formatGoalValue(secondaryGoalValue)}</strong>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`kpi-card ${statusVariant}`}>
      <div className="kpi-card-header">
        <div className="kpi-card-title-wrap">
          <i className={`fas ${getIcon()}`}></i>
          <div className="kpi-card-title">{title}</div>
        </div>
        <span className={`kpi-status-badge ${statusVariant}`}>{statusLabel}</span>
      </div>

      <div className="kpi-card-primary-grid">
        <div className="kpi-card-primary-metric">
          <small>{actualLabel}</small>
          <div className="kpi-card-value">{formatValue(numValue)}</div>
        </div>
        <div className="kpi-card-primary-metric kpi-card-primary-goal">
          <small>{goalLabel}</small>
          <div className="kpi-card-goal-value">{formatGoalValue(numTarget)}</div>
        </div>
      </div>

      {hasSecondary ? (
        <div className="kpi-card-secondary-row">
          <div className="kpi-card-secondary-item">
            <small>{secondaryActualLabel}</small>
            <strong>{formatValue(secondaryActualValue)}</strong>
          </div>
          <div className="kpi-card-secondary-item">
            <small>{secondaryGoalLabel}</small>
            <strong>{formatGoalValue(secondaryGoalValue)}</strong>
          </div>
        </div>
      ) : null}

      <div className="kpi-card-pace-row">
        <span className={`kpi-card-pace-alert ${paceRisk.tone}`}>{paceRisk.label}</span>
        <span className="kpi-card-pace-value">
          Expected pace {formatGoalValue(expectedPaceValue)}
        </span>
      </div>

      <div className="kpi-card-direction-row">
        <span className="kpi-card-direction">{isHigherBetter ? 'Higher is better' : 'Lower is better'}</span>
        <span className="kpi-card-progress-text">{formatGoalPercent(percentToGoal)}</span>
      </div>

      <div className="kpi-card-progress">
        <div className="kpi-card-progress-track">
          <div
            className={`kpi-card-progress-fill ${statusVariant}`}
            style={{ width: `${hasGoal ? progressWidth : 0}%` }}
          />
        </div>
      </div>

      <div className="kpi-card-progress-caption">
        <span>Percent to Goal {formatGoalPercent(percentToGoal)}</span>
        <span>Rolling Progress {formatGoalPercent(rollingProgress)}</span>
      </div>

      <div className="kpi-card-metrics">
        <div className="kpi-card-metric">
          <small>Variance</small>
          <strong>{formatValue(variance)}</strong>
        </div>
        <div className="kpi-card-metric">
          <small>% To Goal</small>
          <strong>{formatGoalPercent(percentToGoal)}</strong>
        </div>
      </div>

      <KpiGapBadge actualValue={numValue} goalValue={numTarget} unit={unit} />
    </div>
  );
}

export default KPICard;
